import React, { useState, useEffect } from "react";
import axios from "axios";
import { AuthGuard } from "../App";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapMarkerAlt,
  faStar,
  faChevronLeft,
  faSpinner, // For loading state
  faExclamationCircle, // For error state
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const SpecificPropertyPage = () => {
  const [propertyInfo, setPropertyInfo] = useState(null);
  const [amenities, setAmenities] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [propertyRatings, setPropertyRatings] = useState(null); // State to hold property ratings, still null initially
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const propertyId = localStorage.getItem("specificPropertyId");

  useEffect(() => {
    if (!propertyId) {
      setError("No property selected. Please navigate from the marketplace.");
      setLoading(false);
      toast.error("Error: No property ID found.");
      return;
    }
    if (!token) {
      setError("Authentication token missing. Please log in.");
      setLoading(false);
      toast.error("Authentication required.");
      return;
    }

    setLoading(true);
    setError(null);

    const fetchAllData = async () => {
      const headers = { Authorization: `Bearer ${token}` };

      try {
        const [
          propertyResponse,
          amenitiesResponse,
          rentalsResponse,
          ratingsResponse, // This will contain data or a default value
        ] = await Promise.all([
          // Fetch property information
          axios.get(
            `https://backend.inyumba.co.ke/iNyumba/admin/properties/records/${propertyId}`,
            { headers }
          ),
          // Fetch amenities
          axios.get(
            `https://backend.inyumba.co.ke/iNyumba/amenities/fetch/${propertyId}`,
            { headers }
          ),
          // Fetch rentals
          axios.get(
            `https://backend.inyumba.co.ke/iNyumba/admin/rentals/property/${propertyId}`,
            { headers }
          ),
          // Fetch property ratings with specific error handling
          axios.get(
            `https://backend.inyumba.co.ke/iNyumba/admin/comments/average-ratings/${propertyId}`,
            { headers }
          ).catch(err => {
            // If fetching ratings fails, log it and return a default response of 0
            console.warn("Error fetching property ratings, defaulting to 0:", err);
            return { data: { response: 0 } }; // Mock a successful response with 0 rating
          }),
        ]);

        setPropertyInfo(propertyResponse.data.response);
        setAmenities(amenitiesResponse.data.response);
        setRentals(rentalsResponse.data.response);
        setPropertyRatings(ratingsResponse.data.response); // This will now be 0 if the ratings fetch failed
        setLoading(false);
      } catch (err) {
        // This catch block will only be hit if propertyInfo, amenities, or rentals fail to fetch.
        // Ratings fetch errors are handled internally in its promise.
        console.error("Error fetching core property data:", err);
        setError("Failed to load property details. Please try again later.");
        setLoading(false);
        toast.error("Failed to load property details.");
      }
    };

    fetchAllData();
  }, [propertyId, token]);

  const handleRentalClick = (rentalId) => {
    localStorage.setItem("specificRentalId", rentalId);
    navigate("/user/specificrental");
  };

  // Helper function to render star ratings
  const renderStars = (rating) => {
    const stars = [];
    // Ensure rating is treated as a number, defaulting to 0 if null
    const numericRating = rating !== null ? rating : 0;
    const fullStars = Math.floor(numericRating);
    // Determine if there's a half star. For simplicity, let's say anything above .25 for half.
    const hasHalfStar = (numericRating % 1) > 0.25 && (numericRating % 1) < 0.75;
    const filledStarsCount = fullStars + (hasHalfStar ? 1 : 0);
    const remainingStars = 5 - filledStarsCount;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <FontAwesomeIcon key={`full-${i}`} icon={faStar} className="text-yellow-400" />
      );
    }
    if (hasHalfStar) {
      stars.push(
        <FontAwesomeIcon key="half" icon={faStar} className="text-yellow-400 opacity-50" />
      );
    }
    for (let i = 0; i < remainingStars; i++) {
      stars.push(
        <FontAwesomeIcon key={`empty-${i}`} icon={faStar} className="text-gray-300" />
      );
    }
    return stars;
  };

  // --- Loading State UI ---
  if (loading) {
    return (
      <AuthGuard>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="flex flex-col items-center text-gray-600 p-8 rounded-lg bg-white shadow-lg">
            <FontAwesomeIcon icon={faSpinner} spin className="text-5xl mb-4 text-indigo-500" />
            <p className="text-xl font-medium">Loading property details...</p>
          </div>
        </div>
      </AuthGuard>
    );
  }

  // --- Error State UI ---
  if (error) {
    return (
      <AuthGuard>
        <div className="min-h-screen flex flex-col items-center justify-center bg-red-50 p-6">
          <div className="flex flex-col items-center text-red-700 p-8 rounded-lg bg-white shadow-lg border border-red-200">
            <FontAwesomeIcon icon={faExclamationCircle} className="text-5xl mb-4" />
            <h2 className="text-2xl font-bold mb-2">Error</h2>
            <p className="text-center mb-4">{error}</p>
            <button
              onClick={() => navigate("/user/propertymarketplace")}
              className="bg-red-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700 transition-colors shadow-md"
            >
              Go to Marketplace
            </button>
          </div>
        </div>
      </AuthGuard>
    );
  }

  // Fallback if propertyInfo is unexpectedly null after loading
  if (!propertyInfo) {
    return (
      <AuthGuard>
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
          <div className="flex flex-col items-center text-gray-600 p-8 rounded-lg bg-white shadow-lg border border-gray-200">
            <FontAwesomeIcon icon={faExclamationCircle} className="text-5xl mb-4 text-gray-500" />
            <h2 className="text-2xl font-bold mb-2">Property Not Found</h2>
            <p className="text-center mb-4">The property you are looking for does not exist or could not be loaded.</p>
            <button
              onClick={() => navigate("/user/propertymarketplace")}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md"
            >
              Go to Marketplace
            </button>
          </div>
        </div>
      </AuthGuard>
    );
  }

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100 p-6 sm:p-8 lg:p-10">
        {/* Back Button */}
        <div className="mb-8 animate-fade-in-down">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 bg-white text-gray-800 rounded-full shadow-md hover:bg-gray-100 transition-colors transform hover:-translate-x-1"
            aria-label="Back to Marketplace"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="mr-2" />
            Back to Marketplace
          </button>
        </div>

        {/* Property Hero Section */}
        <section className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-5xl mx-auto mb-12 animate-fade-in-up">
          <div className="relative h-64 sm:h-80 lg:h-96">
            <img
              src={propertyInfo.photo}
              alt={propertyInfo.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6 sm:p-8">
              <div className="text-white">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-1 sm:mb-2 leading-tight drop-shadow-md">
                  {propertyInfo.title}
                </h1>
                <p className="text-md sm:text-lg lg:text-xl font-medium flex items-center drop-shadow-sm">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-indigo-300" />
                  {propertyInfo.location}, {propertyInfo.county}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 pb-4 border-b border-gray-100">
              <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-0">
                <strong className="font-semibold text-gray-900">Description:</strong>{" "}
                {propertyInfo.description}
              </p>
              <div className="flex items-center text-lg sm:text-xl font-bold text-gray-800 bg-yellow-50 px-4 py-2 rounded-full shadow-sm">
                <span className="mr-2">Rating:</span>
                {renderStars(propertyRatings)}
                <span className="ml-2">
                  {/* Display propertyRatings, defaulting to 0.0/5 if propertyRatings is null initially,
                      or if the fetch errored and defaulted to 0 */}
                  {propertyRatings !== null ? `${propertyRatings.toFixed(1)}/5` : "N/A"}
                </span>
              </div>
            </div>

            {/* Amenities Section */}
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">
              Amenities
            </h2>
            {amenities.length > 0 ? (
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-8">
                {amenities.map((amenity, index) => (
                  <span
                    key={index}
                    className="bg-indigo-50 text-indigo-800 text-sm sm:text-base font-medium px-4 py-2 rounded-full shadow-sm transition-colors duration-200 hover:bg-indigo-100"
                  >
                    {amenity.amenity_name}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-600 mb-8 text-md sm:text-lg">No specific amenities listed for this property.</p>
            )}
          </div>
        </section>

        {/* Available Rental Units Section */}
        <section className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-5xl mx-auto animate-fade-in-up">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8 text-center">
            Available Rental Units
            {propertyInfo.title && <span className="block text-xl font-semibold text-gray-700 mt-2">in {propertyInfo.title}</span>}
          </h2>

          {rentals.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rentals.map((rental) => (
                <div
                  key={rental.rental_id}
                  className="bg-white border border-gray-100 shadow-lg rounded-xl overflow-hidden cursor-pointer
                             hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 group"
                  onClick={() => handleRentalClick(rental.rental_id)}
                  aria-label={`View details for ${rental.property_name}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={rental.photo}
                      alt={rental.property_name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <span className="absolute top-3 left-3 bg-purple-600 text-white text-sm font-bold px-3 py-1 rounded-full shadow-md">
                      {rental.house_type}
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-1">
                      {rental.property_name}
                    </h3>
                    <p className="text-gray-700 text-sm mb-3 line-clamp-2">
                      {rental.description}
                    </p>
                    <div className="text-sm text-gray-600 mb-3">
                      <p className="flex items-center mb-1">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-indigo-500" />
                        <span className="font-semibold">Location:</span> {propertyInfo.location}
                      </p>
                    </div>

                    <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                      <p className="text-xl font-extrabold text-green-600">
                        KES {Number(rental.rent_price).toLocaleString()}
                      </p>
                      <button className="text-indigo-600 hover:text-indigo-800 text-sm font-semibold transition-colors">
                        View Details <FontAwesomeIcon icon={faChevronLeft} rotation={180} className="ml-1 text-xs" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-600 text-lg py-10">
              No rental units currently available in this property.
            </p>
          )}
        </section>
      </div>
    </AuthGuard>
  );
};

export default SpecificPropertyPage;